import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faEnvelope,
  faFile,
  faFileExcel,
  faFileImage,
  faFilePdf,
  faFileWord,
  faReply,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import Markdown from 'react-markdown';

import { Message, MessageAttachment } from '@/types/message';
import DualTime from '@/ui/components/DualTime';
import Tooltip from '@/ui/components/Tooltip';
import { formatBytes } from '@/utils/formatBytes';

import MessageErrorIndicator from './MessageErrorIndicator';
import { EmailMessageProps } from './types';

function attachmentIcon(name: string): { icon: IconDefinition; colorClass: string } {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext))
    return { icon: faFileImage, colorClass: 'text-purple-500' };
  if (ext === 'pdf') return { icon: faFilePdf, colorClass: 'text-red-500' };
  if (['doc', 'docx'].includes(ext)) return { icon: faFileWord, colorClass: 'text-blue-500' };
  if (['xls', 'xlsx'].includes(ext)) return { icon: faFileExcel, colorClass: 'text-green-600' };
  return { icon: faFile, colorClass: 'text-gray-400' };
}

export const AttachmentCard = ({
  a,
  onDownload
}: {
  a: MessageAttachment;
  onDownload?: (attachmentId: string) => Promise<{ url: string }>;
}) => {
  const { icon, colorClass } = attachmentIcon(a.name);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleClick = async () => {
    if (!onDownload || loading) return;
    setLoading(true);
    setError(false);
    try {
      const { url } = await onDownload(a.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const cardClass =
    'inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs min-w-0 max-w-56 transition-colors ' +
    (onDownload
      ? 'cursor-pointer border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300 hover:bg-gray-100'
      : 'border-gray-200 bg-gray-50 text-gray-700');

  return (
    <Tooltip tooltipContent={error ? 'Download failed — try again' : onDownload ? 'Download' : ''}>
      <button
        type="button"
        disabled={!onDownload || loading}
        onClick={() => void handleClick()}
        className={cardClass}>
        <FontAwesomeIcon
          icon={loading ? faSpinner : icon}
          className={`flex-shrink-0 text-base ${loading ? 'animate-spin text-gray-400' : colorClass}`}
        />
        <div className="flex flex-col min-w-0 text-left">
          <span className={`truncate font-medium leading-tight ${error ? 'text-red-500' : ''}`}>
            {a.name}
          </span>
          <span className="text-gray-400 leading-tight">{formatBytes(a.size)}</span>
        </div>
      </button>
    </Tooltip>
  );
};

const EmailMessage = ({ message, isPending, timezone, onReply, onDownload }: EmailMessageProps) => {
  const msg = message as Message;
  const isInbound = !isPending && msg.direction === 'in';

  let handleDownload: ((attachmentId: string) => Promise<{ url: string }>) | undefined;
  if (!isPending && onDownload) {
    handleDownload = (attachmentId: string) => onDownload(attachmentId, msg.kustomer_id);
  }

  return (
    <div
      className={`group flex items-start gap-2 w-full mb-4 ${isInbound ? 'flex-row' : 'flex-row-reverse'}`}>
      <div className="relative flex-shrink-0 mt-1">
        <div
          className={`inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-500 ${isPending ? 'opacity-60' : ''}`}>
          <FontAwesomeIcon className="text-white text-xs" icon={faEnvelope} />
        </div>
        {!isPending && msg.error_message && (
          <MessageErrorIndicator errorMessage={msg.error_message} />
        )}
      </div>
      <div
        className={`min-w-0 max-w-[72%] rounded-xl border bg-white dark:bg-gray-800 shadow-sm overflow-hidden ${
          isInbound
            ? 'border-gray-200 dark:border-gray-700 border-l-4 border-l-gray-300'
            : 'border-gray-200 dark:border-gray-700 border-l-4 border-l-blue-500'
        } ${isPending ? 'opacity-60' : ''}`}>
        <div
          className={`flex items-center flex-wrap justify-between gap-x-3 gap-y-1 px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 ${
            isInbound ? 'bg-gray-50 dark:bg-gray-900' : 'bg-blue-50 dark:bg-blue-950'
          }`}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
              {message.subject ?? '(No subject)'}
            </span>
            <span
              className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full font-medium ${
                isInbound
                  ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
              }`}>
              {isInbound ? 'Received' : 'Sent'}
            </span>
          </div>
          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
            {isPending ? (
              <span className="flex items-center gap-1">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                <span>Sending...</span>
              </span>
            ) : (
              msg.sent_at && <DualTime dateTime={new Date(msg.sent_at)} memberTZ={timezone} />
            )}
          </span>
        </div>
        <div className="px-4 py-3">
          {message.preview ? (
            <div className="prose prose-sm dark:prose-invert max-w-none break-words">
              <Markdown>{message.preview}</Markdown>
            </div>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500 italic">No preview available</p>
          )}
        </div>
        {!isPending && msg.attachments && msg.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 px-4 pb-3">
            {msg.attachments.map((a: MessageAttachment) => (
              <AttachmentCard key={a.id} a={a} onDownload={handleDownload} />
            ))}
          </div>
        )}
        {!isPending && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 dark:border-gray-700">
            <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
              {isInbound ? `From: ${msg.sender}` : `To: ${msg.recipient}`}
              {msg.created_by?.first_name && msg.created_by?.last_name && (
                <span>
                  {' '}
                  · {msg.created_by.first_name} {msg.created_by.last_name}
                </span>
              )}
            </span>
            {onReply && !msg.error_message && (
              <Tooltip tooltipContent="Reply">
                <button
                  onClick={() => onReply(msg)}
                  aria-label="Reply to this email"
                  className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                  type="button">
                  <FontAwesomeIcon icon={faReply} className="text-xs" />
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailMessage;
